import {
  forgotPasswordSchema,
} from "@reelio/contracts";
import { MailCheck } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link, useLocation } from "react-router-dom";

import { authClient } from "./auth-client";
import { getAuthErrorMessage } from "./auth-errors";
import { AuthShell } from "./AuthShell";
import { Field, PrimaryButton } from "./FormControls";

export const ForgotPasswordPage = () => {
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [formError, setFormError] = useState("");
  const [isComplete, setIsComplete] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsed = forgotPasswordSchema.safeParse({ email });

    if (!parsed.success) {
      setEmailError(parsed.error.flatten().fieldErrors.email?.[0] ?? "");
      return;
    }

    setIsSubmitting(true);
    setEmailError("");
    setFormError("");
    try {
      const result = await authClient.requestPasswordReset({
        email: parsed.data.email,
        redirectTo: "/auth/reset-password",
      });
      if (result.error) {
        setFormError(
          getAuthErrorMessage(
            result.error,
            "We could not request a reset link. Try again.",
          ),
        );
        return;
      }
      setIsComplete(true);
    } catch (error) {
      setFormError("We could not request a reset link. Try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthShell
      title="Reset your password"
      description="Enter your account email. If it matches an account, we’ll send a one-hour reset link."
      footer={
        <p>
          Remembered it? <Link to={`/auth/login${location.search}`}>Log in</Link>
        </p>
      }
    >
      {isComplete ? (
        <section className="auth-success" aria-live="polite">
          <span className="auth-success__icon" aria-hidden="true">
            <MailCheck size={21} />
          </span>
          <h2>Check your inbox.</h2>
          <p>
            If an account matches that email, its reset link is on the way.
          </p>
        </section>
      ) : (
        <form className="auth-form" onSubmit={submit} noValidate>
          <Field
            label="Email"
            name="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="you@yourbrand.com"
            value={email}
            error={emailError}
            onChange={(event) => {
              setEmail(event.target.value);
              setEmailError("");
              setFormError("");
            }}
          />

          {formError ? (
            <p className="auth-form__alert" role="alert">
              {formError}
            </p>
          ) : null}

          <PrimaryButton
            type="submit"
            isLoading={isSubmitting}
            loadingLabel="Sending link…"
          >
            Send reset link
          </PrimaryButton>
        </form>
      )}
    </AuthShell>
  );
};
